<script setup lang="ts">
import { watch, ref, onMounted, type Ref } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import {
  loadRootGroupsInternal,
  loadCustomerUsersInternal,
} from '@/api/InternalGroupApi';
import type { Group } from '@/models/Group';
import type { UserInternal } from '@/models/User';
import GroupPanelInternal from '@/components/internalFleet/GroupPanelInternal.vue';

const loadingSpinnerStore = useLoadingSpinnerStore();
const groups = ref<Group[]>([]);
const users = ref<UserInternal[]>([]);
const userUuid = ref('');
const error = ref('');
const panelKey = ref(0);
const openPanels: Ref<number[]> = ref([]);

async function getRootGroups() {
  if (!userUuid.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  error.value = '';
  try {
    groups.value = await loadRootGroupsInternal(userUuid.value);
    panelKey.value++;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    error.value = err.message;
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

async function fetchCustomerUsers() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    users.value = await loadCustomerUsersInternal();
    if (users.value.length > 0) {
      userUuid.value = users.value[0].uuid;
      await getRootGroups();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error('Failed to fetch customer users:', err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

onMounted(async () => {
  await fetchCustomerUsers();
  if (groups.value.length === 1) {
    openPanels.value = [0];
  }
});

watch(userUuid, async () => {
  await getRootGroups();
});
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Robot fleet') }}
        </h1>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="userUuid"
          :items="users"
          item-title="email"
          item-value="uuid"
          label="Select a User"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12" md="8" lg="6" xl="4">
        <v-alert
          v-if="error"
          class="mt-8"
          border="top"
          icon="mdi-close-circle"
          border-color="error"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12">
        <v-expansion-panels :key="panelKey" v-model="openPanels">
          <template v-for="group in groups" :key="group.uuid">
            <GroupPanelInternal :userUuid="userUuid" :group="group" />
          </template>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </div>
</template>
